import { PriceAdd } from "@/types/sales";
import saveAs from "file-saver";
import { TFunction } from "i18next";
import * as XLSX from 'xlsx';


/**
 * 获取供应商价格导入模板表头
 */
export const getCustomerPriceHeaders = (t: TFunction) => [
  t('purchase.excelHeaderInventoryCode'),
  t('purchase.excelHeaderUnitPriceIncludingTax'),
  t('sale.estimateDays'),
  t('sale.expiredAt') + '(xxxx/xx/xx)',
  t('sale.customerModel'),
  t('sale.mfgr'),
  t('sale.reRemark'),
];
/**
 * 生成价格导入模板并下载
 */
export const generateSupplierPriceTemplate = (t: TFunction) => {
  // 获取表头
  const headers = getCustomerPriceHeaders(t);

  // 工作表数据（仅表头）
  const worksheetData = [headers];

  // 创建工作表
  const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);

  // 设置列宽
  worksheet['!cols'] = [
    { wch: 20 },  // 存货编码
    { wch: 15 },  // 含税单价
    { wch: 15 },  // 预计到货天数
    { wch: 24 },  // 过期时间
    { wch: 30 },  // 客户型号
    { wch: 30 },  // 公司型号
    { wch: 40 },  // 描述
  ];

  // 创建工作簿
  const workbook = XLSX.utils.book_new();
  const sheetName = t('sale.customerPriceTemplate');
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

  // 生成Excel文件并下载
  const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const dataBlob = new Blob([excelBuffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8',
  });

  const fileName = `${t('sale.customerPriceTemplate')}.xlsx`;
  saveAs(dataBlob, fileName);
};

/**
 * 获取单元格值并trim
 */
const getCellValue = (row: any[], index: number): string => {
  if (row[index] === undefined || row[index] === null) return '';
  return String(row[index]).trim();
};


//解析表格
export const parseCustomerPriceFile = (file: File, t: TFunction, customerId: string): Promise<PriceAdd[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // 解析为二维数组
        const jsonData = XLSX.utils.sheet_to_json<string[]>(worksheet, { header: 1 });

        if (!jsonData || jsonData.length === 0) {
          reject(new Error('invalid'));
          return;
        }

        // 验证表头
        const headers = jsonData[0];
        const expectedHeaders = getCustomerPriceHeaders(t);
        const isValidHeader = expectedHeaders.every((header, index) => header === headers[index]);
        if (!isValidHeader) {
          reject(new Error('invalid'));
          return;
        }

        // 解析数据行（跳过表头）
        const result: PriceAdd[] = [];
        for (let i = 1; i < jsonData.length; i++) {
          const row = jsonData[i];
          if (!row || row.length === 0) continue;

          result.push({
            customerId,
            productId: getCellValue(row, 0),
            price: getCellValue(row, 1),
            estimateDays: Number(getCellValue(row, 2)) || null,
            expiredAt: null,
            mfgr: getCellValue(row, 4),
            customerModel: getCellValue(row, 5),
            desc: getCellValue(row, 6),
          });
        }

        resolve(result.filter(item => !!item.productId && !!item.price));
      } catch {
        reject(new Error('invalid'));
      }
    };
    reader.onerror = () => reject(new Error('invalid'));
    reader.readAsArrayBuffer(file);
  });
};